import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  buildSapCollateralChangeContentHtml,
  SapCollateralChangeSnapshot,
} from '@/components/Approval/@components/SapCollateralChange';
import { getSapCollateralDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  createSapCollateralChangeState,
  pickSapCollateralRecords,
  SAP_COLLATERAL_MISSING_MESSAGE,
  type SapCollateralDraftSource,
} from '@/utils/approval/sapCollateralChange';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SapCollateralChangeFieldProps = {
  apvType: number;
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true — 그때 sapKey 로 1회 조회 */
  ready: boolean;
};

/**
 * SAP 담보 신규(154)·변경(155) — sapKey 로 SAP 매장 여신을 조회해 신규 / 변경 전·후 표로.
 * 편집으로 다시 열어도 다시 조회하고 제목도 ZTITLE 로 덮어씀(레거시 동일)
 */
const SapCollateralChangeField = ({
  apvType,
  ready,
}: SapCollateralChangeFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'sapCollateralChange' });
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const sapKey = getValues('sapCollateralChange')?.sapKey ?? null;
    if (!sapKey) {
      snackbar.showError({ message: t(SAP_COLLATERAL_MISSING_MESSAGE) });
      return;
    }
    setValue('sapCollateralChange', {
      ...createSapCollateralChangeState(sapKey),
      loading: true,
    });
    getNestedDataFetcher<SapCollateralDraftSource>(
      getSapCollateralDraftSourceUrl(apvType, sapKey),
    )
      .then((res) => {
        if (!aliveRef.current) return;
        const records = pickSapCollateralRecords(apvType, res.display?.source);
        if (!records) throw new Error('empty source');
        const autoSubject = records.after.HEAD?.ZTITLE ?? '';
        setValue('sapCollateralChange', {
          ...createSapCollateralChangeState(sapKey),
          ...records,
          autoSubject,
        });
        setValue('subject', autoSubject, { shouldDirty: false });
      })
      .catch(() => {
        if (!aliveRef.current) return;
        setValue('sapCollateralChange', createSapCollateralChangeState(sapKey));
        snackbar.showError({ message: t(SAP_COLLATERAL_MISSING_MESSAGE) });
      });
  }, [apvType, ready, getValues, setValue, snackbar, t]);

  const content = useMemo(
    () =>
      buildSapCollateralChangeContentHtml({
        apvType,
        before: state?.before ?? null,
        after: state?.after ?? null,
        t,
      }),
    [apvType, state?.before, state?.after, t],
  );

  return (
    <FieldBlock className="sap-collateral-change">
      {state?.loading && (
        <ApprovalNoticeBox role="status" sx={{ mb: 1 }}>
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      )}
      <SapCollateralChangeSnapshot content={content} />
    </FieldBlock>
  );
};

export default SapCollateralChangeField;
